import { useCallback, useEffect, useState } from "react";
import { fmtDateIST } from "@/lib/dates";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = entity !== "all" ? `?entity=${entity}&limit=300` : "?limit=300";
      const { data } = await api.get(`/audit-logs${p}`);
      setLogs(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="audit-logs-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="audit-logs-title">Audit Logs</h1>
          <p className="text-sm text-slate-500 mt-1">Every important change in the system, who made it and when.</p>
        </div>
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger data-testid="audit-filter-entity" className="w-44 h-10 border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="order">Orders</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
            <SelectItem value="staff">Staff</SelectItem>
            <SelectItem value="billing">Billing</SelectItem>
            <SelectItem value="settings">Settings</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        {loading ? (
          <div className="p-6 space-y-3">{[...Array(6)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded-sm animate-pulse" />)}</div>
        ) : logs.length === 0 ? (
          <EmptyState title="No audit records" testId="audit-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {["Date / Time", "User", "Role", "Action", "Entity", "Order", "Field", "Old Value", "New Value"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 even:bg-slate-50/60" data-testid={`audit-log-row-${l.id}`}>
                    <td className="px-4 py-2.5 text-slate-500 text-xs tnum whitespace-nowrap">{fmtDateIST(l.at)}</td>
                    <td className="px-4 py-2.5 font-medium text-slate-800 whitespace-nowrap">{l.user_name}</td>
                    <td className="px-4 py-2.5 text-slate-600 capitalize">{l.role}</td>
                    <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap">{l.action}</td>
                    <td className="px-4 py-2.5 text-slate-600 capitalize">{l.entity}</td>
                    <td className="px-4 py-2.5 text-primary font-medium whitespace-nowrap">{l.order_ref || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{l.field || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap max-w-[160px] truncate">{l.old_value || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-800 whitespace-nowrap max-w-[160px] truncate">{l.new_value || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
